'use client'

import { useTranslations } from 'next-intl'
import { useSprints, useSprintsFromMultipleProjects, DbSprintWithProject } from '@/hooks/use-sprints'
import type { DbSprint } from '@/lib/types/db-sprint'
import { statusToString } from '@/lib/types/db-sprint'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Clock, PlayCircle, CheckCircle2, XCircle, FolderTree } from 'lucide-react'
import { format } from 'date-fns'

interface SprintSelectorProps {
  projectId: number | null
  value: string | null
  onChange: (sprintId: string | null, sprintProjectId?: number) => void
  showOnlyActive?: boolean
  allowClear?: boolean
  disabled?: boolean
  placeholder?: string
  /** Include sprints from parent projects in the hierarchy */
  includeParentProjects?: boolean
  /** Array of parent project IDs (ordered from immediate parent to root) */
  parentProjectIds?: number[]
  /** Map of project IDs to project names for display */
  projectNames?: Record<number, string>
}

export function SprintSelector({
  projectId,
  value,
  onChange,
  showOnlyActive = false,
  allowClear = true,
  disabled = false,
  placeholder = 'Select sprint...',
  includeParentProjects = false,
  parentProjectIds = [],
  projectNames = {}
}: SprintSelectorProps) {
  const t = useTranslations('sprintsPage.tasks')
  // Get all project IDs to fetch sprints from (current + parents)
  const allProjectIds = projectId 
    ? [projectId, ...parentProjectIds]
    : parentProjectIds

  // Use multi-project hook if hierarchy is enabled and we have parent projects
  const shouldUseMultiProject = includeParentProjects && allProjectIds.length > 1

  // Fetch sprints from single project (standard behavior)
  const { sprints: singleProjectSprints, loading: singleLoading } = useSprints(
    shouldUseMultiProject ? null : projectId,
    showOnlyActive ? { status: 'active' } : undefined
  )

  // Fetch sprints from multiple projects (hierarchy behavior)
  const { sprints: multiProjectSprints, loading: multiLoading } = useSprintsFromMultipleProjects(
    shouldUseMultiProject ? allProjectIds : null,
    showOnlyActive ? { status: 'active' } : undefined,
    projectNames
  )

  const loading = shouldUseMultiProject ? multiLoading : singleLoading
  const sprints: (DbSprint | DbSprintWithProject)[] = shouldUseMultiProject
    ? multiProjectSprints
    : singleProjectSprints

  const getStatusIcon = (sprint: DbSprint | DbSprintWithProject) => {
    const status = statusToString(sprint.status)
    switch (status) {
      case 'active':
        return <PlayCircle className="h-4 w-4 text-green-500" />
      case 'closed':
        return <CheckCircle2 className="h-4 w-4 text-blue-500" />
      default:
        return <Clock className="h-4 w-4 text-gray-500" />
    }
  }

  const formatSprintDateRange = (sprint: DbSprint | DbSprintWithProject) => {
    if (!sprint.startDate || !sprint.endDate) {
      return 'No dates'
    }
    const startDate = new Date(sprint.startDate)
    const endDate = new Date(sprint.endDate)
    return `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d, yyyy')}`
  }

  const handleValueChange = (newValue: string) => {
    if (newValue === 'none' && allowClear) {
      onChange(null)
    } else {
      // Find the sprint to get its project ID
      const selectedSprint = sprints.find(s => s.id.toString() === newValue)
      onChange(newValue, selectedSprint?.projectId)
    }
  }

  // Check if sprint is from a different (parent) project
  const isFromParentProject = (sprint: DbSprint | DbSprintWithProject) => {
    return sprint.projectId !== projectId
  }

  // Get project name for display
  const getProjectDisplayName = (sprint: DbSprint | DbSprintWithProject) => {
    if ('projectName' in sprint && sprint.projectName) {
      return sprint.projectName
    }
    return projectNames[sprint.projectId] || `Project ${sprint.projectId}`
  }

  if (loading) {
    return (
      <Select disabled>
        <SelectTrigger>
          <SelectValue placeholder={t('loadingSprints')} />
        </SelectTrigger>
      </Select>
    )
  }

  if (!projectId && !includeParentProjects) {
    return (
      <Select disabled>
        <SelectTrigger>
          <SelectValue placeholder={t('selectProjectFirst')} />
        </SelectTrigger>
      </Select>
    )
  }

  return (
    <Select
      value={value || (allowClear ? 'none' : undefined)}
      onValueChange={handleValueChange}
      disabled={disabled || sprints.length === 0}
    >
      <SelectTrigger>
        <SelectValue placeholder={sprints.length === 0 ? 'No sprints available' : placeholder} />
      </SelectTrigger>
      <SelectContent>
        {allowClear && (
          <SelectItem value="none">
            <span className="text-muted-foreground">No sprint</span>
          </SelectItem>
        )}
        {sprints.map((sprint) => {
          const fromParent = isFromParentProject(sprint)
          return (
            <SelectItem key={`${sprint.projectId}-${sprint.id}`} value={sprint.id.toString()}>
              <div className="flex items-center gap-2">
                {getStatusIcon(sprint)}
                <div className="flex flex-col">
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium">{sprint.name}</span>
                    {fromParent && includeParentProjects && (
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                        <FolderTree className="h-3 w-3" />
                        {getProjectDisplayName(sprint)}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {formatSprintDateRange(sprint)}
                  </span>
                </div>
              </div>
            </SelectItem>
          )
        })}
      </SelectContent>
    </Select>
  )
}
